/**
 * L'Hôtel des Ventes (GDD §20).
 *
 * Trois volets sous un même toit : ce que vendent les autres, ce que vend le
 * PNJ, et ce qu'on a soi-même mis en vente. Le PNJ n'est pas relégué en bas de
 * page — à deux joueurs actifs, c'est lui qui fait vivre l'écran.
 */
import type { Ctx } from "../contexte.ts"
import { sousOnglets } from "../contexte.ts"
import { item } from "../../sim/contenu.ts"
import { prixVente } from "../../sim/recolte.ts"
import { prixPNJ, prixUnitaire, TAXE, type Annonce } from "../../sim/marche.ts"
import { tempsPourUn } from "../../sim/rarete.ts"
import { slotDe, perfection, LIBELLE_SLOT, type Porte } from "../../sim/objets.ts"
import { esc, nb } from "../fmt.ts"
import { icone, ic } from "../sprites.ts"
import { lignesJet, lignesFourchette } from "../jet.ts"
import { panoplieDeLObjet } from "./panoplie.ts"
import { TYPES_EQ, PALIERS_NIVEAU } from "./sac.ts"

export const SOUS_MARCHE: [string, string][] = [
  ["etal", "Étal"], ["pnj", "Comptoir"], ["mes", "Mes ventes"],
]

export interface EtatMarche {
  annonces: Annonce[] | null
  enveloppe: { reste: number; annonces: number } | null
  erreur: string | null
  /** l'objet qu'on est en train de mettre en vente */
  vend: { item: number; uid: string | null; quantite: number } | null
  /** ce que le PNJ demande, une fois interrogé */
  chargement: boolean
}

export const marcheVide = (): EtatMarche =>
  ({ annonces: null, enveloppe: null, erreur: null, vend: null, chargement: false })

export function vueMarche(c: Ctx): string {
  // Un second niveau d'onglets : le marché vit sous l'onglet Sac, qui a déjà
  // les siens. Le mot-clé `sousmarche` les distingue.
  const sous = SOUS_MARCHE.some(([k]) => k === c.sousMarche) ? c.sousMarche : "etal"
  const m = c.marche
  if (m.vend) return formulaireVente(c)
  return `${sousOnglets(sous, SOUS_MARCHE, "sousmarche")}
    ${m.erreur ? `<p class="secur alerte">${esc(m.erreur)}</p>` : ""}
    ${sous === "pnj" ? comptoir(c) : sous === "mes" ? mesVentes(c) : etal(c)}`
}

/** La bannière d'enveloppe : une limite invisible passe pour une panne. */
function bandeau(c: Ctx): string {
  const e = c.marche.enveloppe
  if (!e) return ""
  return `<p class="aide enveloppe">
    Droit de dépôt restant : <b>${nb(Math.round(e.reste))} ${ic("kamas", 14)}</b> de marchandise,
    ${e.annonces} annonce${e.annonces > 1 ? "s" : ""} de libre.
    <em>Le marché est la seule partie du jeu que le serveur arbitre : on n'y dépose pas plus que sa partie ne pouvait produire.</em>
  </p>`
}

/** Tris de l'étal. « Récent » d'abord : c'est ce qui vient d'arriver qui intrigue. */
export const TRIS_ETAL: [string, string][] = [
  ["recent", "Récent"], ["prix", "Prix"], ["unitaire", "Prix / unité"],
  ["niveau", "Niveau"], ["perfection", "Perfection"],
]

/**
 * L'étal, avec de quoi chercher dedans.
 *
 * La première version était une liste à plat : pas de filtre, pas de tri, et
 * surtout **pas de statistiques**. Or acheter une pièce forgée sans voir son jet
 * n'a aucun sens — c'est précisément le jet qu'on paie. Quatre lots de Millet
 * identiques se suivaient sans qu'on puisse les distinguer d'un anneau à 163 %.
 *
 * Le filtrage se fait ici, sur les annonces déjà chargées, et non côté serveur :
 * le client possède tout le contenu du jeu, le serveur n'a que des identifiants.
 * C'est tenable tant que l'étal tient dans une page ; le jour où il en faudra
 * plusieurs, il faudra descendre les filtres dans la requête.
 */
function etal(c: Ctx): string {
  const { contenu, marche } = c
  const champ = `<input class="recherche" id="rechercheMarche" type="search"
    placeholder="Chercher un objet en vente" value="${esc(c.recherche)}" />`

  if (marche.annonces === null) {
    return `<section class="carte">${champ}<p class="aide">Chargement de l'étal…</p></section>`
  }

  const toutes = marche.annonces
    .map((a) => ({ a, it: item(contenu, a.item) }))
    .filter((x): x is { a: Annonce; it: NonNullable<typeof x.it> } => Boolean(x.it))
  const visibles = trier(filtrer(c, toutes), c.triEtal)

  return `
  <section class="carte">
    <h2>En vente <em>${visibles.length}${visibles.length !== toutes.length ? ` sur ${toutes.length}` : ""}</em></h2>
    ${champ}
    ${filtresEtal(c, toutes)}
    <nav class="tris"><span>Trier</span>${TRIS_ETAL.map(([k, lib]) =>
      `<button data-trietal="${k}" aria-current="${k === c.triEtal}">${lib}</button>`).join("")}</nav>
    <div class="tranche">
      <label>Niveau de <select data-nivetalmin>${PALIERS_NIVEAU.map((n) =>
        `<option value="${n}" ${n === c.nivEtal[0] ? "selected" : ""}>${n}</option>`).join("")}</select></label>
      <label>à <select data-nivetalmax>${PALIERS_NIVEAU.map((n) =>
        `<option value="${n}" ${n === c.nivEtal[1] ? "selected" : ""}>${n}</option>`).join("")}</select></label>
    </div>
    ${bandeau(c)}
    ${toutes.length === 0
      ? `<p class="aide">L'étal est vide. Le Comptoir, lui, a toujours du stock — et tes propres ventes iront ici.</p>`
      : visibles.length === 0
        ? `<p class="aide">Rien à ce filtre. Élargis la tranche de niveau ou change de catégorie.</p>`
        : `<div class="liste">${visibles.map(({ a, it }) => rangeeAnnonce(c, a, it)).join("")}</div>`}
  </section>`
}

/** À quelle catégorie appartient cet objet : un emplacement, ou « ressource ». */
const categorieDe = (it: NonNullable<ReturnType<typeof item>>): string => {
  const sl = slotDe(it)
  if (!sl) return "ressource"
  return TYPES_EQ.find(([, , slots]) => slots.includes(sl))?.[0] ?? "ressource"
}

function filtrer(
  c: Ctx, l: { a: Annonce; it: NonNullable<ReturnType<typeof item>> }[],
): { a: Annonce; it: NonNullable<ReturnType<typeof item>> }[] {
  const [min, max] = c.nivEtal
  return l
    .filter((x) => c.filtreEtal === "tout" || categorieDe(x.it) === c.filtreEtal)
    .filter((x) => x.it.niveau >= min && x.it.niveau <= max)
}

function trier(
  l: { a: Annonce; it: NonNullable<ReturnType<typeof item>> }[], tri: string,
): typeof l {
  const par = [...l]
  if (tri === "prix") return par.sort((x, y) => x.a.prix - y.a.prix)
  if (tri === "unitaire") return par.sort((x, y) => prixUnitaire(x.a) - prixUnitaire(y.a))
  if (tri === "niveau") return par.sort((x, y) => y.it.niveau - x.it.niveau)
  // Une ressource n'a pas de jet : elle passe après les pièces, à ce tri-là.
  if (tri === "perfection") return par.sort((x, y) => (y.a.perfection ?? -1) - (x.a.perfection ?? -1))
  return par.sort((x, y) => y.a.deposeLe - x.a.deposeLe)
}

/** Les puces de catégorie, avec leur compte : une catégorie vide se voit. */
function filtresEtal(c: Ctx, l: { a: Annonce; it: NonNullable<ReturnType<typeof item>> }[]): string {
  const compte = new Map<string, number>()
  for (const x of l) compte.set(categorieDe(x.it), (compte.get(categorieDe(x.it)) ?? 0) + 1)
  const puce = (k: string, lib: string, n: number | null) =>
    `<button data-filtreetal="${k}" aria-current="${k === c.filtreEtal}"${n === 0 ? " disabled" : ""}
      >${lib}${n ? ` <em>${n}</em>` : ""}</button>`
  return `<nav class="filtres">
    ${puce("tout", "Tout", l.length || null)}
    ${puce("ressource", "Ressources", compte.get("ressource") ?? 0)}
    ${TYPES_EQ.map(([k, lib]) => puce(k, lib, compte.get(k) ?? 0)).join("")}
  </nav>`
}

/**
 * Une annonce : ce qu'on voit d'un coup d'œil, et ce qu'on voit en la touchant.
 *
 * Repliée, elle tient sur deux lignes. Dépliée, elle montre **le jet** — la
 * seule chose qui distingue deux exemplaires du même objet, et donc la seule qui
 * justifie l'écart de prix —, la comparaison avec ce qu'on porte, et la panoplie
 * dont la pièce fait partie.
 */
function rangeeAnnonce(c: Ctx, a: Annonce, it: NonNullable<ReturnType<typeof item>>): string {
  const { etat, contenu, p } = c
  const mien = a.vendeur === idLocal()
  const assez = etat.kamas >= a.prix
  const ouverte = c.annonceOuverte === a.id
  const porte: Porte | null = a.jet ? { uid: `a${a.id}`, item: a.item, jet: a.jet } : null
  const slot = slotDe(it)
  const actuel = p && slot ? p.equipement[slot] : undefined
  const itActuel = actuel ? item(contenu, actuel.item) : null
  // Au-dessus de cent, la pièce a été poussée à la forge au-delà de son maximum
  // (GDD §19) : ça ne se fabrique pas par hasard, ça se signale.
  const surpuissante = (a.perfection ?? 0) > 100

  // Le prix en clair sur la rangée : l'afficher ne suffit pas à le lire, la
  // cellule mêle le total et le prix à l'unité.
  return `<div class="annonce ${ouverte ? "ouverte" : ""} ${surpuissante ? "surpuissante" : ""}"
    data-prix="${a.prix}" data-niveau="${it.niveau}" data-perfection="${a.perfection ?? ""}">
    <button class="entete" data-annonce="${a.id}" aria-expanded="${ouverte}"
      title="${porte ? "Voir le jet" : "Voir le détail"}">
      <span class="vignette" style="${icone("items", it.id, 40)}"></span>
      <span class="txt">
        <span class="nom">${esc(it.nom)}${a.quantite > 1 ? ` <em>×${a.quantite}</em>` : ""}</span>
        <span class="niv">niv ${it.niveau}${
          a.perfection !== null ? ` · <b class="pf ${surpuissante ? "sur" : a.perfection >= 90 ? "haut" : ""}">${a.perfection} %</b>` : ""}${
          a.pseudo ? ` · ${esc(a.pseudo)}` : ""}</span>
      </span>
      <span class="prix">${nb(a.prix)} ${ic("kamas", 14)}${
        a.quantite > 1 ? `<em>${nb(prixUnitaire(a))} / unité</em>` : ""}</span>
      <span class="chevron">${ouverte ? "▴" : "▾"}</span>
    </button>

    ${ouverte ? `<div class="detailAnnonce">
      ${porte
        ? lignesJet(contenu, porte)
        // **Sans jet, on montre quand même ce que l'objet donne.** Une annonce
        // sans jet n'affichait que « lot de N » : un familier, un objet à effets
        // fixes, on ne savait pas ce qu'on achetait. « Par exemple le gelano on
        // ne sait pas les stats qu'il donne » (Vyshu, 27/08/2026). Les
        // ressources n'ont pas d'effet et n'affichent donc toujours rien : c'est
        // juste, il n'y a rien à dire d'un lot de blé.
        : `${a.quantite > 1 ? `<p class="aide">Lot de ${nb(a.quantite)} × ${esc(it.nom)}.</p>` : ""}
           ${lignesFourchette(contenu, it.id)}`}
      ${porte && itActuel && actuel
        ? `<p class="compare ${(a.perfection ?? 0) >= perfection(itActuel, actuel) ? "mieux" : "moins"}">
            En place : ${esc(itActuel.nom)}, ${perfection(itActuel, actuel)} %.
            ${(a.perfection ?? 0) >= perfection(itActuel, actuel) ? "Celle-ci est meilleure." : "Celle-ci est moins bonne."}
          </p>`
        : porte && slot ? `<p class="aide">Ton emplacement ${esc(LIBELLE_SLOT[slot])} est libre.</p>` : ""}
      ${porte && p && it.niveau > p.niveau
        ? `<p class="secur alerte">Il te faut le niveau ${it.niveau} pour la porter.</p>` : ""}
      ${panoplieDeLObjet(c, it.id)}
    </div>` : ""}

    <span class="action">${mien
      ? `<button class="mini" data-retirerannonce="${a.id}">Retirer</button>`
      : `<button class="mini ${assez ? "primaire" : ""}" data-acheterannonce="${a.id}" ${assez ? "" : "disabled"}
          >${assez ? "Acheter" : "Trop cher"}</button>`}</span>
  </div>`
}

/** L'identifiant du compte, lu là où le jeu le range. */
const idLocal = () => localStorage.getItem("dofusidle.compte") ?? ""

/**
 * Le comptoir du PNJ : il vend tout, toujours, de plus en plus cher.
 *
 * On ne montre que ce qui sert vraiment — les ressources — et pas les pièces
 * d'équipement : celles-là se craftent ou se gagnent, les acheter toutes faites
 * viderait le jeu de son contenu.
 */
function comptoir(c: Ctx): string {
  const { contenu, etat } = c
  const q = c.recherche.trim().toLowerCase()
  // Seul ce que le jeu donne vraiment : un objet sans provenance n'a pas de
  // temps d'obtention, donc pas de prix, et le comptoir n'en a pas.
  const liste = Object.values(contenu.items)
    .filter((it) => !slotDe(it) && it.niveau > 0)
    .filter((it) => q.length >= 2 && it.nom.toLowerCase().includes(q))
    .map((it) => ({ it, prix: prixPNJ(contenu, it) }))
    .filter((x): x is { it: typeof x.it; prix: number } => x.prix !== null)
    .sort((a, b) => a.prix - b.prix)
    .slice(0, 40)

  return `
  <section class="carte">
    <h2>Comptoir</h2>
    <p class="aide">Le comptoir facture <b>le temps</b> qu'il te fait gagner :
      le prix d'un objet, c'est ce que tu aurais gagné en allant le chercher
      toi-même, multiplié par trois. Un objet qui tombe à 6 % sur un boss de
      donjon coûte donc cent fois une ressource qu'on ramasse par terre, même à
      niveau égal. Il monte encore d'un cran toutes les cent unités achetées.
      <em>À revenu égal, farmer reste meilleur ; le comptoir devient intéressant
      quand tu es très au-dessus de ce que tu achètes.</em></p>
    <input class="recherche" id="rechercheMarche" type="search"
      placeholder="Chercher une ressource à acheter" value="${esc(c.recherche)}" />
    ${q.length < 2
      ? `<p class="aide">Tape au moins deux lettres : le comptoir a tout le jeu en réserve.</p>`
      : liste.length === 0
        ? `<p class="aide">Rien de ce nom-là au comptoir.</p>`
        : `<div class="liste">${liste.map(({ it, prix }) => {
            const assez = etat.kamas >= prix
            return `<div class="rangee annonce">
              <span class="vignette" style="${icone("items", it.id, 40)}"></span>
              <span class="nom">${esc(it.nom)}</span>
              <span class="niv">niv ${it.niveau} · ${esc(dureeLisible(tempsPourUn(contenu, it) ?? 0))} à farmer · rachat ${nb(prixVente(it))}</span>
              <span class="prix">${nb(prix)} ${ic("kamas", 14)}<em>l'unité</em></span>
              <div class="lots">
                <button class="mini ${assez ? "primaire" : ""}" data-pnj="${it.id}" data-q="1" ${assez ? "" : "disabled"}>×1</button>
                <button class="mini" data-pnj="${it.id}" data-q="10" ${etat.kamas >= prix * 10 ? "" : "disabled"}>×10</button>
                <button class="mini" data-pnj="${it.id}" data-q="100" ${etat.kamas >= prix * 100 ? "" : "disabled"}>×100</button>
              </div>
            </div>`
          }).join("")}</div>`}
  </section>`
}

function mesVentes(c: Ctx): string {
  const { marche } = c
  const miennes = (marche.annonces ?? []).filter((a: Annonce) => a.vendeur === idLocal())
  return `
  <section class="carte">
    <h2>Mes ventes <em>${miennes.length}</em></h2>
    ${bandeau(c)}
    ${miennes.length === 0
      ? `<p class="aide">Rien en vente. Depuis le sac, une pièce du coffre ou un lot de ressources se met en vente en deux gestes.</p>`
      : `<div class="liste">${miennes.map((a: Annonce) => { const it = item(c.contenu, a.item); return it ? rangeeAnnonce(c, a, it) : "" }).join("")}</div>`}
    <p class="aide">Sur chaque vente, l'Hôtel prélève ${Math.round(TAXE * 100)} % — comme dans Dofus.
      Cette part est <b>détruite</b>, pas reversée : c'est ce qui empêche deux comptes complices
      de se servir du marché comme d'une pompe à kamas.</p>
  </section>`
}

/** Le formulaire de mise en vente, ouvert depuis le sac. */
export function formulaireVente(c: Ctx): string {
  const v = c.marche.vend
  if (!v) return ""
  const it = item(c.contenu, v.item)
  if (!it) return ""
  const stock = v.uid ? 1 : (c.etat.inventaire[it.id] ?? 0)
  const porte = v.uid ? c.etat.coffre.find((x) => x.uid === v.uid) : null

  return `
  <section class="carte vente">
    <div class="entete">
      <span class="ic" style="${icone("items", it.id, 48)}"></span>
      <div class="ident">
        <b>${esc(it.nom)}</b>
        <span>niv ${it.niveau} · le comptoir rachète à ${nb(prixVente(it))} l'unité</span>
      </div>
      <button class="mini" id="fermerVente">Fermer</button>
    </div>
    ${porte ? lignesJet(c.contenu, porte) : ""}
    <form id="formVente" class="formVente">
      ${v.uid ? "" : `<label>Quantité
        <input name="quantite" type="number" min="1" max="${stock}" value="${Math.min(v.quantite, stock)}" />
        <em>sur ${nb(stock)} en réserve</em></label>`}
      <label>Prix total demandé
        <input name="prix" type="number" min="1" value="${nb(prixVente(it) * (v.uid ? 2 : Math.min(v.quantite, stock))).replace(/\s/g, "")}" />
        <em>tu fixes ton prix ; personne n'est obligé de l'accepter</em></label>
      <button class="primaire" type="submit">Mettre en vente</button>
    </form>
  </section>`
}

/** « 3 s », « 12 min », « 2 h 30 » : le temps qu'on s'épargne, en clair. */
function dureeLisible(s: number): string {
  if (s < 60) return `${Math.round(s)} s`
  if (s < 3600) return `${Math.round(s / 60)} min`
  const h = Math.floor(s / 3600)
  const m = Math.round((s % 3600) / 60)
  return m > 0 ? `${h} h ${m}` : `${h} h`
}
